import React from 'react';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';

// A report that fails on the server must say so. Most pages render "no data"
// when their query errors, so a 500 read as an empty report - the standing
// rule is that an empty state never hides a 500. This is the net under every
// page: a server error (or no answer at all) on a read shows once, plainly.
// A page that handles its own error can opt out with meta: { silent: true }.
const lastShown = new Map();
function reportReadFailure(error, query) {
  if (query?.meta?.silent) return;
  // A prefetch (lib/route-preload) for a page nobody has opened yet: nobody is
  // reading it, and the page that does open refetches and reports its own.
  if (query?.meta?.prefetch && !query.getObserversCount()) return;
  const status = error?.response?.status;
  const noAnswer = !error?.response && (error?.code === 'ERR_NETWORK' || error?.code === 'ECONNABORTED');
  if (!(status >= 500 || noAnswer)) return;
  const key = query?.queryHash || String(status);
  const now = Date.now();
  if (now - (lastShown.get(key) || 0) < 60_000) return;
  lastShown.set(key, now);
  // The server words a 5xx in plain language and adds a short reference
  // (api middleware/friendlyErrors); the reference is what support looks up.
  const why = error?.response?.data?.error;
  const ref = error?.response?.data?.ref;
  toast({
    variant: 'destructive',
    title: noAnswer ? 'Part of this page could not load' : 'Part of this page failed to load',
    description: `${noAnswer ? 'The server did not answer in time.' : (why ? String(why).slice(0, 160) : 'The server returned an error.')} What is shown may be incomplete; refresh to try again.${ref ? ` Reference ${ref}.` : ''}`,
  });
}

// A gateway blip (502/503/504, or no answer) is the server restarting or busy
// for a few seconds: retry three times over ~7s before saying anything, so a
// release going out never reaches a reader as an error. Any other 5xx gets one
// retry; a 4xx is an answer, never retried.
function retryRead(count, error) {
  const status = error?.response?.status;
  const gateway = !error?.response || status === 502 || status === 503 || status === 504;
  if (gateway) return count < 3;
  return status >= 500 && count < 1;
}

// One client for the life of the page, made at module load rather than inside
// the provider, so the boot prefetch (lib/route-preload) can start a report's
// request before React has rendered anything - and the page's own useQuery
// then finds that request already in flight under the same key.
export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: reportReadFailure }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,            // 30s - fresh enough for sales workflow
      refetchOnWindowFocus: true,   // re-check when sales rep returns to tab
      retry: retryRead,
      retryDelay: (n) => Math.min(1000 * 2 ** n, 8000),
    },
    mutations: {
      retry: 0,
    },
  },
});

export function QueryProvider({ children }) {
  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}
